/**
 * PlayerConnection — one WebRTC session per media_server carrying every camera
 * of the player that lives on that server (the server's multi-provider support:
 * data-channel `add`/`remove`, per-deviceId commands). A single-camera view is
 * the one-device case of the same session.
 *
 * A plain class, no React; `usePlayerStreams` owns the instances. Contracts
 * this client is built on (verified server-side):
 *
 *  - The signaling WebSocket must stay open for the whole session. Every
 *    `add`/`remove` triggers an SDP renegotiation whose offer arrives over
 *    the SAME WS, and an added provider only starts when the answer arrives
 *    there. So: never close the WS while the player is open, and answer
 *    every incoming `msg.sdp` — the initial offer and every renegotiation
 *    take the same code path. Offers are answered strictly in arrival order
 *    (a promise chain serializes setRemoteDescription).
 *  - If the WS drops while media is flowing, the media plane survives but
 *    the session's topology is frozen server-side (its tracker is gone
 *    forever). Tiles keep playing; the next topology change (add/remove) or
 *    a 301 stream status forces a full rebuild with the current device set.
 *  - Track↔camera demux: the msid stream id is the camera UUID with dashes
 *    stripped (`event.streams[0].id`). mid is recycled server-side and is
 *    never a key.
 *  - The single-camera connection pool in VideoPlayer is deliberately NOT
 *    used: pooling a multi-provider session would hold every provider for
 *    the pool TTL, and its restore path answers no renegotiation offers.
 */

import { api } from '@/api';
import { publishMetadata } from '@/api/metadataEvents';
import { createDataChannelDecoder, createVideoMessageDecoder } from '@/api/playerDataChannel';
import { createSignallingDecoder } from '@/api/playerSignalling';
import { MILLISECOND_US } from '@/lib/timeUnits';
import { getConnectionRetryPlan } from '@/lib/webrtc/connection-retry';
import { adaptH264ProfilesToBrowser } from '@/lib/webrtc/h264Profile';
import { convertRtpToUtcMs, kMaxSyncEntries } from '@/lib/webrtc/rtpTime';
import type { PlannedCommand } from '@/types/commandPlanner';
import type { MetadataPacket } from '@/types/metadataPacket';
import type {
    DataChannelVideoTrack, DeviceStreamInfo, PlayerDiagnostics, PlayerState, SyncPoint,
} from '@/types/player';
import { ConnectionPhase, StreamQuality } from '@/types/player';
import type {
    CodecUnsupportedFrame, DataChannelFrame, IceFrame, OfferFrame, PositionsFrame,
    StreamStatusFrame, SyncPointFrame, VideoMessage, VideoTransportFrame,
} from '@/types/webrtc';
import { StreamStatus, VideoTransport } from '@/types/webrtc';

import { CommandPlanner } from './commandPlanner';
import { findAudioSyncPoint, readAudioClock } from './dataChannelVideo/audioClock';
import { createDataChannelVideoTrack } from './dataChannelVideo/dataChannelVideoTrack';

/** One camera of the session, and which of its two streams to carry. */
interface DeviceStream {
    deviceId: string;
    quality?: StreamQuality;
}

interface PlayerConnectionOpts {
    /** Relay target; every device here lives on it. */
    serverId: string;
    devices: DeviceStream[];
    onStreams: (streams: Map<string, MediaStream>) => void;
    onState: (state: PlayerState) => void;
    /** The cameras of the session opened (true) or closed (false) their metadata channel. */
    onPublishers: (ids: string[], isOpen: boolean) => void;
    /** The ICE servers for a new peer connection: the STUN list, plus TURN once known. */
    getIceServers: () => RTCIceServer[];
    /** The device's archive ended while the session plays on; once per episode. */
    onStreamEof?: (deviceId: string) => void;
    /** A repositioning went on the wire: to positionMs (null for live), precise or rough. */
    onCommandSent?: (positionMs: number | null, isPrecise: boolean) => void;
    /** Where paused playback settled after the frame step sent with seq. */
    onPosition?: (seq: number, positionsMsByDevice: Map<string, number>) => void;
    /** The player's playhead (epoch ms), or null when live. */
    getResumePositionMs?: () => number | null;
}

/** The seek awaiting its frames; null when none. */
interface SeekGate {
    /** The 32-bit wire the server rebases every provider's first post-seek packet to. */
    base32: number;
    targetMs: number;
    isPrecise: boolean;
    /** Bases of seeks this one superseded before showing. The worker passes one landing of each
     *  (shouldKeepPriorFrames), and a frame at or after any of them reveals once, so a rapid
     *  scrub shows a frame per seek instead of freezing until the last. */
    priorBases: number[];
}

/**
 * A device's reveal latches for the pending seek. It reveals when both hold,
 * and then plays freely — the latches stay set for the rest of this seek.
 */
interface GateProgress {
    isRtpReached: boolean;
    isUtcReached: boolean;
}

/** One receiver's FIFO-watermark gate: the worker, its probe clone and the stream it serves. */
interface VideoGate {
    worker: Worker;
    probeTrack: MediaStreamTrack;
    streamId: string;
}

/** A presented frame's absolute time and whether it is past the pending seek's gate. */
interface FrameResolution {
    utcMs: number;
    isPastGate: boolean;
}

const kSdpHandshakeTimeoutMs = 20_000;

/** The wait before a stall may rebuild the session again; each rebuild for a camera doubles it. */
const STALL_RETRY_MS = 15_000;
/** Fresh sessions that a camera with no picture gets before its tile reports a failure. */
const STALL_REBUILDS = 4;

/**
 * The server answers every seek/pause/step with a data-channel status message. Silence this
 * long after such a command means the session is gone server-side while the transport still
 * looks alive - send() only buffers into SCTP and cannot tell. Generous: the reply waits only
 * for the archive reposition, never for frames.
 */
const kCommandReplyTimeoutMs = 3000;

/**
 * How long a camera set or a seek may keep the next command waiting for its frames. A frame
 * that never comes (an empty archive, a hiccup) must not wedge the player on it.
 */
const COMMAND_SHOWN_TIMEOUT_MS = 3000;

/** 'high' ↔ stream 0 (primary), 'low' ↔ stream 1 (secondary). */
const streamIndexOf = (quality: StreamQuality): number => (quality === StreamQuality.High ? 0 : 1);

const stripUuid = (id: string): string => String(id).replace(/[-{}]/g, '').toLowerCase();

/**
 * RTP-timeline rebase for seek reveal (VMS-166). On each seek the CLIENT picks a
 * 32-bit RTP base a fixed gap above the newest wire timestamp of every track and
 * asks the server (via {seq, rtpBase} on the seek message) to restart every
 * provider's outgoing RTP clock there for its first post-seek packet. Then a
 * presented frame is post-seek once its RAW wire timestamp is at or after the
 * base - within half a wrap ahead of it: the gap places every track's pre-seek
 * flush frames far below it, so they read as (almost a full wrap) ahead and
 * fall on the other half. Raw 32-bit throughout - no per-track unwrap, so
 * independent per-camera RTP offsets can never desync the gate.
 *
 * Why the client picks the base and not the server: the reveal signal then rides
 * the video stream itself - it IS the RTP timestamp of the frames. The server
 * never has to announce "your seek landed" over the data channel. Such an
 * announcement would be a separate message racing the video and metadata streams:
 * it could arrive before its frames (reveal a stale picture) or after them
 * (stutter), and it would have to be paired back to the right seek. Choosing the
 * base up front makes the reveal a property of the pixels the client already
 * decodes - self-timed and race-free against both streams, with no ack round trip.
 */
const kSeekRtpGapTicks = 9_000_000; //< 100 s at the 90 kHz RTP clock; separates flush frames from post-seek ones.
const kRtp32 = 4294967296; //< 2^32 - the 32-bit RTP timestamp wrap period.
const kMaxPriorBases = 16; //< Cap on remembered superseded seek bases; no burst stacks that many.

/** Forward distance base→wire on the 32-bit RTP circle (0 .. 2^32 - 1). */
function getRtpForwardDistance(wire: number, base: number): number {
    return ((wire >>> 0) - (base >>> 0)) >>> 0;
}

/** True when wire is at or after base on the RTP circle: less than half a wrap ahead of it. A
 *  frame a gap below the base (a pre-seek flush frame) sits on the other half. */
function isRtpAtOrAfter(wire: number, base: number): boolean {
    return getRtpForwardDistance(wire, base) < kRtp32 / 2;
}

/**
 * Chooses the 32-bit RTP base the server rebases every track to for a seek (see the module
 * comment). The base must be placed so that no track's PRE-seek flush frames read as at or
 * after it - otherwise a flush frame of some camera would be mistaken for that camera's
 * post-seek frame and reveal a stale picture.
 *
 * A flush frame sits near its own track's newest wire, so the requirement is: keep every
 * track's wire a full gap below the base. On the RTP circle "below" means the forward distance
 * from the base to the wire is large (nearly a full wrap); the forbidden case is a wire only a
 * little AHEAD of the base - forwardDistance(wire, base) < gap - because then that track's
 * flush frames read as post-seek. The gap (100 s) dwarfs the few seconds a flush burst spans,
 * so clearing the gap clears it with wide margin.
 *
 * `maxWire + gap` satisfies this for every track in the common case: all wires are at or below
 * maxWire, hence a gap-or-more below the base. The one exception is when `maxWire + gap` wraps
 * past 2^32 to a small value that happens to sit just below a low-offset track - then that
 * track is a hair AHEAD of the base and collides. The loop detects any such clash and jumps the
 * base a further gap forward, past that track, until a clear slot is found.
 *
 * wires are raw 32-bit values (empty on a cold open -> the base is just the gap). Terminates in
 * at most wires.length + 1 steps: N tracks occupy at most N gap-wide forbidden zones on the
 * ~47 700 s ring, so a free slot always exists within N+1 gap-sized jumps.
 */
export function chooseRtpBase(wires: number[]): number {
    let maxWire = 0;
    for (const wire of wires) {
        maxWire = Math.max(maxWire, wire >>> 0);
    }
    let base = (maxWire + kSeekRtpGapTicks) % kRtp32;
    for (let i = 0; i <= wires.length; i++) {
        const hasClash = wires.some((w) => getRtpForwardDistance(w, base) < kSeekRtpGapTicks);
        if (!hasClash) { break; }
        base = (base + kSeekRtpGapTicks) % kRtp32;
    }
    return base;
}

const kCodecDisplayNames: Record<string, string> = {
    hevc: 'H.265', h265: 'H.265', h264: 'H.264', vp8: 'VP8', vp9: 'VP9',
};

/** The user-facing name of a codec as the server reports it (FFmpeg's short name). */
export function getCodecDisplayName(codec: string | undefined): string {
    const key = String(codec || '').toLowerCase();
    return kCodecDisplayNames[key] || (codec ? String(codec).toUpperCase() : 'This codec');
}

/** The one-line title shown over the video when the browser rejected the stream's codec. */
export function getCodecUnsupportedText(codec: string | undefined): string {
    return `${getCodecDisplayName(codec)} isn't supported in this browser`;
}

/**
 * Whether the page runs on Chrome's engine. Edge, Brave and Opera carry the token too and
 * decode the same codecs, so a switch to Chrome would not help them either.
 */
export function isChromeBrowser(): boolean {
    return /Chrome\//.test(navigator.userAgent);
}

/** What the user can do about it; shown under the title where there is room. */
export function getCodecUnsupportedHint(
    codec: string | undefined, isChrome: boolean = isChromeBrowser(),
): string {
    const change = getCodecDisplayName(codec) === 'H.264'
        ? 'Change camera video settings'
        : 'Switch the camera to H.264 in its video settings';
    return `${change}, or open this page in ${isChrome ? 'another browser' : 'Chrome'}`;
}

/**
 * A frame presented within this long of a device's end-of-archive report is
 * the pipeline draining what the server sent before the report - not proof
 * the camera delivers again. Later frames clear the device's ended mark.
 */
const kEofDrainMs = 2000;

/**
 * A camera UUID as its 16 raw RFC-4122 bytes (the hex in written order), the prefix a talk-back
 * audio frame carries so the media_server routes it by device (matches nx::Uuid::fromRfc4122).
 * Null for a malformed id.
 */
const kUuidBytes = 16;
const kUuidHexChars = kUuidBytes * 2;
const kHexRadix = 16;

function uuidToBytes(uuid: string): Uint8Array | null {
    const hex = uuid.replace(/-/g, '');
    if (hex.length !== kUuidHexChars) { return null; }
    const bytes = new Uint8Array(kUuidBytes);
    for (let i = 0; i < kUuidBytes; i += 1) {
        const byte = parseInt(hex.slice(i * 2, i * 2 + 2), kHexRadix);
        if (Number.isNaN(byte)) { return null; }
        bytes[i] = byte;
    }
    return bytes;
}

/** How often a data channel video track hears the audio clock of its camera. */
const AUDIO_CLOCK_MS = 250;

export class PlayerConnection {
    serverId: string;

    onStreams: (streams: Map<string, MediaStream>) => void;

    onState: (state: PlayerState) => void;

    onPublishers: (ids: string[], isOpen: boolean) => void;

    private getIceServers: () => RTCIceServer[];

    onStreamEof?: (deviceId: string) => void;

    onCommandSent?: (positionMs: number | null, isPrecise: boolean) => void;

    onPosition?: (seq: number, positionsMsByDevice: Map<string, number>) => void;

    getResumePositionMs: () => number | null;

    /** What the player wants against what the wire has, and the commands that close the gap. */
    planner: CommandPlanner;

    /** The seq of the last camera set sent; the server's reply echoing it settles its arrivals. */
    switchSeq: number | null = null;

    shouldBePaused = false;

    wasPauseSent = false;

    commandSeq = 0;

    seekSeq: number | null = null;

    syncMaps = new Map<string, SyncPoint[]>();

    lastWire = new Map<string, number>();

    seekGate: SeekGate | null = null;

    gateProgress = new Map<string, GateProgress>();

    seekAckMs = new Map<string, number>();

    lastBase32: number | null = null;

    switchBases = new Map<string, number>();

    endedDevices = new Map<string, number>();

    codecUnsupportedByDevice = new Map<string, string>();

    /** deviceId → the stream its tile deserves, as the tiles report it; the hook's debounced
   *  monitor commits it to the planner once it has held still. */
    qualities: Map<string, StreamQuality>;

    metadataOn = new Map<string, boolean>();

    byStrippedUuid: Map<string, string>;

    streams = new Map<string, MediaStream>();

    /** deviceId → where its video travels, as the server last said; RTP when it said nothing. */
    videoTransports = new Map<string, VideoTransport>();

    /** deviceId → the picture of a camera whose video comes over the data channel. */
    dataChannelVideo = new Map<string, DataChannelVideoTrack>();

    midByStreamId = new Map<string, number>();

    isClosed = false;

    phase: ConnectionPhase = ConnectionPhase.Idle;

    isTopologyFrozen = false;

    retryAttempt = 0;

    retryTimer: ReturnType<typeof setTimeout> | undefined;

    /** Sends the data channel video tracks the audio playing now; runs while there is one. */
    audioClockTimer?: ReturnType<typeof setInterval>;

    handshakeTimer: ReturnType<typeof setTimeout> | undefined;

    replyTimer: ReturnType<typeof setTimeout> | undefined;

    /** The seq of the command the reply watchdog waits on; meaningful while replyTimer runs. */
    replyAwaitSeq = 0;

    sdpChain: Promise<void> = Promise.resolve();

    videoGates = new Set<VideoGate>();

    /** deviceId → when its stall last rebuilt the session, the next wait, and the rebuilds. */
    stallRetries = new Map<string, { atMs: number; waitMs: number; rebuilds: number }>();

    /** When a stall last rebuilt the session, for any camera. */
    stallRebuildAtMs = -Infinity;

    /** The cameras that showed no picture on STALL_REBUILDS fresh sessions. */
    noPicture = new Set<string>();

    /** The quality every camera was opened at by the session URL. */
    urlQuality: StreamQuality = StreamQuality.Low;

    pc: RTCPeerConnection | null = null;

    ws: WebSocket | null = null;

    dc: RTCDataChannel | null = null;

    /**
   * @param {object} opts
   * @param {string} opts.serverId Relay target; every device here lives on it.
   * @param {Array<{deviceId: string, quality: 'low'|'high'}>} opts.devices
   * @param {(streams: Map<string, MediaStream>) => void} opts.onStreams
   * @param {(state: PlayerState) => void} opts.onState
   *   Cameras whose codec the browser rejected ride in codecUnsupportedByDevice.
   * @param {(ids: string[], open: boolean) => void} opts.onPublishers The cameras of the
   *   session opened or closed their metadata channel.
   * @param {() => RTCIceServer[]} opts.getIceServers The ICE servers of a new peer connection.
   * @param {(deviceId: string) => void} [opts.onStreamEof] The device's archive
   *   ended while the session plays on (server-sent, once per episode).
   * @param {(positionMs: number | null, precise: boolean) => void} [opts.onCommandSent] A
   *   repositioning went on the wire.
   * @param {(seq: number, positionsMsByDevice: Map<string, number>) => void} [opts.onPosition] Where
   *   paused playback settled after the frame step sent with seq.
   */
    constructor({
        serverId, devices, onStreams, onState, onPublishers, getIceServers, onStreamEof,
        onCommandSent, onPosition, getResumePositionMs,
    }: PlayerConnectionOpts) {
        this.serverId = serverId;
        this.onStreams = onStreams;
        this.onState = onState;
        this.onPublishers = onPublishers;
        this.getIceServers = getIceServers;
        this.onStreamEof = onStreamEof;
        this.onCommandSent = onCommandSent;
        this.onPosition = onPosition;
        /** Returns the player's current playhead (epoch ms) or null when live —
     *  a rebuilt/late-joining session resumes THERE, not at the original
     *  seek target issued minutes ago. */
        this.getResumePositionMs = getResumePositionMs ?? (() => null);
        this.planner = new CommandPlanner({
            send: (command): boolean => this.execute(command),
            hasStream: (deviceId): boolean => this.streams.has(deviceId),
            commandTimeoutMs: COMMAND_SHOWN_TIMEOUT_MS,
        });
        for (const device of devices) {
            this.planner.desiredQualities.set(device.deviceId,
                device.quality ?? StreamQuality.Low);
        }
        /** A connection born while the player is parked in the archive (a camera from a new
     *  server dropped in mid-playback) must come up AT the shared playhead, not live - the
     *  planner delivers the seek once the channel opens. */
        this.planner.desiredPositionMs = this.getResumePositionMs();
        this.shouldBePaused = false;
        this.wasPauseSent = false;
        /** Client-chosen sequence of the last command that expects a reply. */
        this.commandSeq = 0;
        /** Sequence of the last seek; only the 200 echoing it acknowledges the seek. */
        this.seekSeq = null;
        /** deviceId → [{rtpTs, utcUs}] newest-last, for frame→UTC mapping. */
        this.syncMaps = new Map();
        /** deviceId → newest presented raw 32-bit wire RTP; a seek's base goes above the max. */
        this.lastWire = new Map();
        /**
     * The seek awaiting its frames: {base32, targetMs, precise}, null when none.
     * base32 is the 32-bit wire the server rebases every provider's first
     * post-seek packet to.
     */
        this.seekGate = null;
        /**
     * Per-device reveal latches for the pending seek: deviceId → {rtp, utc}. The
     * rtp latch trips once a frame at or after the seek's base lands (post-seek
     * region reached); the utc latch trips once a precise seek reaches the
     * requested time (a rough seek has none). A device reveals when both hold,
     * and then plays freely - the latches stay set for the rest of this seek.
     */
        this.gateProgress = new Map();
        /** deviceId → UTC (ms) the server acked the current seek at ({status:200,
     *  timestampMs}); the diagnostics HUD compares it against the requested time
     *  to surface a seek that landed wrong. Persists until the next seek. */
        this.seekAckMs = new Map();
        /** The RTP base of the newest repositioning, so the next one lands a gap above it even
     *  when none of its frames has been presented yet (see nextRtpBase). */
        this.lastBase32 = null;
        /**
     * deviceId → the RTP base a camera switch rebased this arriving camera to.
     * A retarget reuses the m-line - the same track, the same decoder - so the
     * picture up is the camera that LEFT until a frame at this base lands.
     * Present means "not that camera yet"; dropped on the first frame past it.
     */
        this.switchBases = new Map();
        /**
     * deviceId → Date.now() of the server's end-of-archive report (status 410)
     * for devices that delivered nothing since. The server holds such a camera
     * until the next seek; the diagnostics show the mark.
     */
        this.endedDevices = new Map();
        /**
     * deviceId → the codec the server reported the browser had rejected for it.
     * A rejection is one camera's, so only that camera's tile reports it. It lasts
     * while the camera stays in this session: a new session reports its own.
     */
        this.codecUnsupportedByDevice = new Map();
        /** deviceId → the desired per-device stream. */
        this.qualities = new Map(devices.map((d) => [d.deviceId, d.quality ?? StreamQuality.Low]));
        /** deviceId → true (desired overlay-metadata subscription). */
        this.metadataOn = new Map();
        /** stripped uuid → deviceId, for ontrack demux. */
        this.byStrippedUuid = new Map(
            [...this.qualities.keys()].map((id) => [stripUuid(id), id]));
        this.streams = new Map(); //< deviceId → MediaStream.
        /** MediaStream id → mid of its video m-line. A retarget keeps both, so the entry outlives
     *  the device the m-line was opened for. */
        this.midByStreamId = new Map();
        this.isClosed = false;
        /** Last phase emitted ('idle' before the first connect): the diagnostics HUD
     *  reads it so a silent tile can be told apart from an unconnected one. */
        this.phase = ConnectionPhase.Idle;
        this.isTopologyFrozen = false;
        this.retryAttempt = 0;
        this.retryTimer = undefined;
        this.handshakeTimer = undefined;
        this.replyTimer = undefined;
        this.sdpChain = Promise.resolve();
        /** Per-video-receiver FIFO-watermark gate state (holds the in-flight FIFO, drain waiters and
     *  the probe clone), tracked here so teardownPeer can stop the clones on a rebuild. */
        this.videoGates = new Set();
        this.connect();
    }

    getDeviceIds(): string[] {
        return [...this.qualities.keys()];
    }

    connect(): void {
        if (this.isClosed) { return; }
        const ids = this.getDeviceIds();
        if (ids.length === 0) { return; }

        this.teardownPeer();
        this.isTopologyFrozen = false;
        this.emitState(ConnectionPhase.Connecting);

        const firstText = ids[0];
        // A fresh session starts live/playing with a fresh RTP timeline: forget what the old one
        // was told and let the planner reissue the archive state once the new channel opens.
        this.wasPauseSent = false;
        this.syncMaps.clear();
        this.endedDevices.clear();
        // The server names the cameras it refuses after every answer, so a new session brings its
        // own list - and a refusal the old one carried may no longer stand.
        this.codecUnsupportedByDevice.clear();
        this.resetRtpTimeline();
        this.urlQuality =
            (firstText === undefined ? undefined
                : this.qualities.get(firstText)) ?? StreamQuality.Low;
        // Every camera is opened by the URL below at urlQuality, live, so that is what the wire
        // has: the planner corrects each tile to its own quality and the archive position once
        // the channel opens. Their metadata publishers register now - client bookkeeping tied to
        // the URL request, not the data channel.
        this.onPublishers(this.getDeviceIds(), true);
        this.planner.onSessionOpened(this.getDeviceIds(), this.urlQuality);
        // Open the whole layout in one request: the URL camera is the comma-separated device list
        // (first = primary), so the server builds every camera before the initial offer and the grid
        // negotiates in one round instead of an add-per-camera.
        const wsUrl = api.getWebrtcUrl(this.getDeviceIds().join(','), this.urlQuality, {
            serverId: this.serverId,
            withMetadata: false,
        });
        const decodeSignal = createSignallingDecoder(wsUrl, this.serverId);
        const decodeDataChannel = createDataChannelDecoder(wsUrl, this.serverId);
        const decodeVideoMessage = createVideoMessageDecoder(wsUrl, this.serverId);
        const pc = new RTCPeerConnection({
            iceServers: this.getIceServers(),
            iceCandidatePoolSize: 4,
        });
        this.pc = pc;

        pc.ontrack = (event) => {
            const stream = event.streams?.[0];
            if (!stream) { return; }
            const deviceId = this.byStrippedUuid.get(stripUuid(stream.id));
            if (!deviceId) { return; }
            // A track on the camera's own m-line never carried the camera it replaced.
            if (stripUuid(stream.id) === stripUuid(deviceId)) {
                this.switchBases.delete(deviceId);
            }
            // Decoder backpressure by a drop-robust FIFO watermark, run in a worker (see
            // videoGate.worker.js) so main-thread jank cannot starve the reader and desync the count.
            // The encoded input goes through RTCRtpScriptTransform; the decoded output is read off a
            // CLONE of the track (so <video> keeps the original and is never starved), transferred to the
            // same worker where input and output share one FIFO.
            if (event.track.kind === 'video' && 'RTCRtpScriptTransform' in window &&
                typeof window.MediaStreamTrackProcessor === 'function') {
                // Declared outside the try, so a failed setup can stop whatever it already started.
                let worker: Worker | undefined;
                let probeTrack: MediaStreamTrack | undefined;
                try {
                    worker = new Worker(
                        new URL('./videoGate.worker.ts', import.meta.url), { type: 'module' });
                    probeTrack = event.track.clone();
                    // MediaStreamTrack is not transferable here, so build the processor on this thread and
                    // transfer its readable - the reader loop and FIFO still run in the worker.
                    const proc = new window.MediaStreamTrackProcessor({ track: probeTrack });
                    worker.postMessage({ readable: proc.readable }, [proc.readable]);
                    event.receiver.transform = new RTCRtpScriptTransform(worker, { name: 'gate' });
                    worker.postMessage({ shouldPace: true }); //< Pace the startup GOP burst.
                    this.videoGates.add({ worker, probeTrack, streamId: stream.id });
                } catch (e) {
                    this.stopVideoGate({ worker, probeTrack });
                    console.warn('worker gate setup failed', e instanceof Error ? e.message : e);
                }
            }
            if (event.track.kind === 'video') {
                this.midByStreamId.set(stream.id, Number(event.transceiver?.mid));
            }
            this.streams.set(deviceId, stream);
            this.publishStreams();
        };

        pc.ondatachannel = (event) => {
            this.dc = event.channel;
            event.channel.binaryType = 'arraybuffer';
            const onOpen = () => this.onDataChannelOpen();
            if (event.channel.readyState === 'open') {
                onOpen();
            } else {
                event.channel.onopen = onOpen;
            }
            event.channel.onmessage = (message: MessageEvent<string | ArrayBuffer>) => {
                if (message.data instanceof ArrayBuffer) {
                    const video = decodeVideoMessage(message.data);
                    if (video !== null) { this.onVideoMessage(video); }
                    return;
                }
                const frame = decodeDataChannel(message.data);
                if (frame !== null) { this.onDataChannelFrame(frame); }
            };
        };

        pc.onicecandidate = (event) => {
            if (event.candidate && this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({
                    ice: {
                        candidate: event.candidate.candidate,
                        sdpMid: event.candidate.sdpMid,
                        sdpMLineIndex: event.candidate.sdpMLineIndex,
                        usernameFragment: event.candidate.usernameFragment,
                    },
                }));
            }
        };

        pc.onconnectionstatechange = () => {
            if (this.isClosed || this.pc !== pc) { return; }
            // A transient 'failed'/'disconnected' can recover on its own (a later,
            // often TURN-relayed, pair validates late) — cancel the armed rebuild
            // once connected so the recovery isn't torn down by it.
            if (pc.connectionState === 'connected') {
                this.retryAttempt = 0;
                this.cancelRetry();
                this.emitState(ConnectionPhase.Connected);
            } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
                this.scheduleRetry();
            }
        };

        const ws = new WebSocket(wsUrl);
        this.ws = ws;
        ws.onerror = () => ws.close();
        ws.onclose = () => {
            if (this.isClosed || this.ws !== ws) { return; }
            if (pc.connectionState === 'connected') {
                // Media survives, but the server released its tracker: no more
                // renegotiations on this session. Rebuild on the next topology
                // change instead of now — the user keeps watching.
                this.isTopologyFrozen = true;
            } else {
                this.scheduleRetry();
            }
        };

        clearTimeout(this.handshakeTimer);
        this.handshakeTimer = setTimeout(() => {
            if (this.isClosed || this.pc !== pc || pc.connectionState === 'connected') { return; }
            this.scheduleRetry();
        }, kSdpHandshakeTimeoutMs);

        ws.onmessage = (event: MessageEvent<string>) => {
            const signal = decodeSignal(event.data);
            if (signal === null) { return; }
            switch (signal.key) {
                case 'sdp':
                    this.answerOffer(pc, ws, signal.frame);
                    break;
                case 'ice':
                    this.addCandidate(pc, signal.frame);
                    break;
                case 'error':
                    this.rejectCodec(signal.frame);
                    break;
                default:
                    break;
            }
        };
    }

    /**
     * Answers an offer, the initial one or a renegotiation. The handshake timer is deliberately
     * NOT cleared here: it must cover ICE too, not just offer arrival, so a session that exchanges
     * SDP but stalls in `connecting` still retries. Its callback no-ops once connectionState is
     * `connected`.
     */
    answerOffer(pc: RTCPeerConnection, ws: WebSocket, offer: OfferFrame): void {
        // Serialize: the server versions its offers and each must be answered
        // in arrival order (renegotiations for every add/remove ride here).
        this.sdpChain = this.sdpChain.then(async () => {
            if (this.isClosed || this.pc !== pc) { return; }
            try {
                // The server offers each camera's own H.264 profile; a browser that only
                // receives the constrained spelling gets the offer respelled for it.
                await pc.setRemoteDescription(new RTCSessionDescription({
                    ...offer.sdp,
                    sdp: adaptH264ProfilesToBrowser(offer.sdp.sdp),
                }));
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                // offerId goes back as it came: the server tells by it which offer this
                // answers, and drops the answer to one it has since replaced.
                ws.send(JSON.stringify({
                    sdp: {
                        type: answer.type,
                        sdp: answer.sdp,
                    },
                    ...(offer.offerId === undefined ? {} : { offerId: offer.offerId }),
                }));
            } catch (err) {
                console.debug(
                    '[PlayerConnection] SDP handshake aborted',
                    err instanceof Error ? err.name : err,
                );
                if (this.pc === pc) { this.scheduleRetry(); }
            }
        });
    }

    addCandidate(pc: RTCPeerConnection, frame: IceFrame): void {
        // RTCIceCandidateInit takes the media line id as a string.
        const candidate = {
            ...frame.ice,
            sdpMid: String(frame.ice.sdpMid),
        };
        pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
    }

    /**
     * One camera the browser cannot decode and the server cannot transcode. The session plays on
     * for the rest, so nothing here touches the connection - the camera is named, and its own tile
     * is the one that says so.
     */
    rejectCodec(frame: CodecUnsupportedFrame): void {
        console.warn('[PlayerConnection] codec unsupported by this browser', frame);
        const rejectedId = this.byStrippedUuid.get(stripUuid(frame.deviceId));
        if (rejectedId) {
            this.codecUnsupportedByDevice.set(rejectedId, frame.codec);
            this.emitState(this.phase);
        }
    }

    onDataChannelOpen(): void {
        if (this.isClosed) { return; }
        for (const [id, isOn] of this.metadataOn) {
            if (isOn) { this.send({ metadata: true, deviceId: id }); }
        }
        // Pause is re-asserted on its own: a live pause has no seek, but must survive a rebuild.
        if (this.shouldBePaused && !this.wasPauseSent) {
            let ok = true;
            for (const id of this.getDeviceIds()) { ok = this.send({ pause: id }) && ok; }
            this.wasPauseSent = ok;
        }
        // The URL opened the cameras of the moment, live, at one quality: whatever the player
        // wants beyond that - cameras that came or went while the handshake ran, each tile's
        // quality, the archive position - is the difference the planner sends now.
        // A rebuilt session resumes at the playhead: it has moved on since the last seek.
        const resumeMs = this.getResumePositionMs();
        if (this.planner.desiredPositionMs !== null && resumeMs !== null) {
            this.planner.setDesiredPosition(resumeMs, /*precise*/ true);
        }
        this.planner.plan();
    }

    /**
   * Seeks every provider of this session to positionMs (epoch ms); null
   * returns everything to live. The server broadcasts a null-deviceId seek
   * to all providers and rebases them onto one RTP timeline. precise lands
   * on the exact time (the server pre-rolls from the keyframe, the tiles hide
   * the run); false lands rough on the preceding keyframe with nothing to hide.
   */
    seekAll(positionMs: number | null, isPrecise = true): void {
        if (this.isClosed) { return; }
        this.planner.setDesiredPosition(positionMs, isPrecise);
        this.planner.plan();
    }

    /**
   * Puts a command the planner decided on onto the wire; false when the channel cannot take it
   * now, so the planner keeps it due.
   */
    execute(command: PlannedCommand): boolean {
        if (this.isClosed) { return false; }
        if (this.dc?.readyState !== 'open') {
            // A session on its way up is NOT interrupted: tearing it down to carry one more camera
            // only restarts the handshake, and a burst of adds then never lets one finish.
            // onDataChannelOpen plans again once the channel is open, and a failed attempt
            // retries from connect(), which reads the same desired state.
            const isOpening = this.pc && (!this.dc || this.dc.readyState === 'connecting');
            if (!isOpening && !this.retryTimer) { this.rebuild(); }
            return false;
        }
        switch (command.kind) {
            case 'cameras':
                // A frozen session cannot renegotiate: its tracker is gone server-side.
                if (this.isTopologyFrozen) {
                    this.rebuild();
                    return false;
                }
                return this.applyDeviceSet(command.added, command.removed,
                    command.positionMs);
            case 'seek':
                if (!this.sendRepositioning(command.positionMs,
                    command.isPrecise)) { return false; }
                this.onCommandSent?.(command.positionMs, command.isPrecise);
                return true;
            case 'live':
                if (!this.sendRepositioning(null, true)) { return false; }
                this.onCommandSent?.(null, true);
                return true;
            case 'quality': {
                // The seq lets its 200 reply be told from a seek's.
                const seq = ++this.commandSeq;
                const message = {
                    deviceId: command.deviceId, stream: streamIndexOf(command.quality), seq,
                };
                if (!this.send(message)) { return false; }
                console.debug(
                    `[PlayerConnection] quality ${command.deviceId} -> ${command.quality}`);
                return true;
            }
            default:
                return false;
        }
    }

    /**
   * Sends a position to the cameras on the wire: a seek, or the return to live for null.
   * False when the channel took nothing, so the position stays due.
   */
    sendRepositioning(positionMs: number | null, isPrecise: boolean): boolean {
        // Old sync points map the pre-seek RTP timeline; the post-seek BOF report
        // replaces them.
        this.syncMaps.clear();
        if (positionMs === null) {
            this.endedDevices.clear(); //< The end was the archive's; live delivers.
            // Live carries a fresh base, the same a seek does: the server rebases every provider
            // onto a known timeline instead of continuing a stale one. A switch to a camera with
            // no archive leaves the muxer's armed rebase unfired, so live frames keep the
            // pre-switch timeline and freeze the decoder until a rebuild - a base the server can
            // rebase to is what a bare live seek lacked. The pre-live flush stays a gap below the
            // base and is dropped; live has no target time, so its gate reveals on the first
            // frame past the base.
            const base32 = this.getNextRtpBase();
            const seq = ++this.commandSeq;
            if (!this.send({ seek: -1, seq, rtpBase: base32 })) { return false; }
            this.setRepositionGate(-1, /*precise*/ false, seq, base32);
            this.startReplyWatchdog(seq);
            return true;
        }
        return this.sendSeek(positionMs, isPrecise);
    }

    /**
   * Records positionMs as the position the player wants WITHOUT planning a command: the
   * device change about to follow plans, and carries it in its own command, so the cameras
   * are positioned once. Seeking here first would reposition the
   * camera on its way out to a moment nobody will see, and the server needs
   * seconds to recover from the two arriving together.
   */
    parkSeek(positionMs: number): void {
        if (this.isClosed) {
            return;
        }
        this.planner.setDesiredPosition(positionMs, /*precise*/ true);
        this.syncMaps.clear();
        this.seekAckMs.clear();
        // The switch gates the camera it brings by its own base (switchBases). A
        // precise seek gate still armed from before would hold that camera off for
        // good: its frames never reach that seek's requested time.
        this.seekGate = null;
        this.gateProgress = new Map();
    }

    /** Arms the reveal gate for a seek and sends it; false if the channel is not open. */
    sendSeek(positionMs: number, isPrecise: boolean): boolean {
    // A base clear of every track's wire, so the server rebases all providers there and each
    // track's flush frames stay a gap below the base (see chooseRtpBase).
        const base32 = this.getNextRtpBase();
        const seq = ++this.commandSeq;
        if (!this.send({ seek: positionMs, seq, rtpBase: base32, keyframe: !isPrecise })) {
            return false;
        }
        this.setRepositionGate(positionMs, isPrecise, seq, base32);
        this.startReplyWatchdog(seq);
        return true;
    }

    /**
   * Arms the reveal machinery for a repositioning already sent with seq/base32: every
   * receiver drops pre-reposition frames below base32 and each tile reveals on its first
   * frame past the gate - shared by a seek and a positioned resume.
   */
    setRepositionGate(
        positionMs: number, isPrecise: boolean, seq: number, base32: number,
    ): void {
        // An archive seek keeps the landing of a seek it supersedes, so a scrub shows a frame per
        // seek; live restarts the RTP clock low, so it drops to the new base outright.
        const shouldKeepPriorFrames = positionMs >= 0;
        const prior = this.seekGate;
        const priorBases = shouldKeepPriorFrames && prior
            ? [prior.base32, ...prior.priorBases].slice(-kMaxPriorBases)
            : [];
        this.videoGates.forEach((p) => p.worker.postMessage({
            dropBelow: base32, shouldKeepPriorFrames,
        }));
        this.seekSeq = seq;
        this.seekGate = { base32, targetMs: positionMs, isPrecise, priorBases };
        this.gateProgress = new Map();
        this.seekAckMs.clear();
        // A camera still arriving by retarget stays gated: its tile must keep showing nothing of
        // the camera that left, and the seek latches OPEN once its frames land - a late in-flight
        // frame of the departed camera would then pass them. Carry each pending switch onto this
        // repositioning's own base: the server rebases every provider here, so the arriving
        // camera's first post-reposition frame lifts it and everything older stays hidden.
        for (const id of this.switchBases.keys()) {
            this.switchBases.set(id, base32);
        }
    }

    /**
   * The base for the next repositioning: a gap above every presented frame AND above the
   * previous base. The previous stream may still be in flight with none of its frames
   * presented yet - a scrub step right after a click - and a base chosen from presented
   * frames alone would land inside it: its key frame below the new base (dropped) and its
   * delta frames above (accepted, undecodable), stalling the decoder with nothing to present
   * and nothing to move the wires, so every later seek would pick the same base again.
   */
    getNextRtpBase(): number {
        const wires = [...this.lastWire.values()];
        if (this.lastBase32 !== null) {
            wires.push(this.lastBase32);
        }
        this.lastBase32 = chooseRtpBase(wires);
        return this.lastBase32;
    }

    /** Forgets the RTP wire history and any pending gate - for a fresh session or a
   *  return to live, where the timeline restarts and every frame reveals. Switch bases go
   *  too: live restarts the RTP clock low, so a switch base held over from the archive would
   *  sit forever above the arriving camera's live frames and never let them show. */
    resetRtpTimeline(): void {
        this.lastWire.clear();
        this.lastBase32 = null;
        this.seekGate = null;
        this.gateProgress = new Map();
        this.switchBases.clear();
    }

    /**
   * Classifies a presented frame of deviceId by its RTP timestamp: its absolute
   * time (0 before the first sync point) and whether it is past the pending
   * seek's gate. Two latches per device: the rtp latch trips once a frame at or
   * after the seek's base lands (post-seek region reached, pre-seek flush
   * excluded); the utc latch trips once a precise seek reaches the
   * requested time (a rough seek has none). Past = both latched; with no seek
   * pending every frame of a camera the session carries is past.
   */
    resolveFrame(tileDeviceId: string, rtpTimestamp: number, streamId?: string): FrameResolution {
        // A tile is labelled with the camera the player wants, but the frames it presents are the
        // camera on the wire of its stream: a switch still on its way has yet to change that, and
        // the camera it brings is settled by the frames of the one it replaces.
        const wireDeviceId = streamId === undefined
            ? undefined : this.byStrippedUuid.get(stripUuid(streamId));
        const deviceId = wireDeviceId ?? tileDeviceId;
        if (!this.planner.onWire.has(deviceId)) { return { utcMs: 0, isPastGate: false }; }
        const wire = rtpTimestamp >>> 0;
        this.lastWire.set(deviceId, wire);
        // A picture well after the end-of-archive report means the camera delivers
        // again - the session is usable.
        const endedAt = this.endedDevices.get(deviceId);
        if (endedAt !== undefined && Date.now() - endedAt > kEofDrainMs) {
            this.endedDevices.delete(deviceId);
        }
        const utcMs = convertRtpToUtcMs(wire, this.getSyncMap(deviceId));
        // A camera that arrived by retarget inherited the picture of the one it
        // replaced; hold the tile off it until a frame at the switch's base lands.
        const switchBase = this.switchBases.get(deviceId);
        if (switchBase !== undefined) {
            if (!isRtpAtOrAfter(wire, switchBase)) { return { utcMs, isPastGate: false }; }
            this.switchBases.delete(deviceId);
        }
        const gate = this.seekGate;
        if (!gate) {
            this.clearStall(deviceId);
            this.planner.markDelivered(deviceId);
            return { utcMs, isPastGate: true };
        }

        // A frame the worker let through for a seek this one superseded (shouldKeepPriorFrames):
        // below the newest base but at or after an earlier one. Show it once so a rapid scrub
        // keeps updating the picture, without settling (the ack already did) or tripping the
        // newest latches.
        if (!isRtpAtOrAfter(wire, gate.base32) &&
            gate.priorBases.some((base) => isRtpAtOrAfter(wire, base))) {
            return { utcMs, isPastGate: true };
        }

        let progress = this.gateProgress.get(deviceId);
        if (!progress) {
            progress = { isRtpReached: false, isUtcReached: !gate.isPrecise };
            this.gateProgress.set(deviceId, progress);
        }
        if (!progress.isRtpReached && isRtpAtOrAfter(wire, gate.base32)) {
            progress.isRtpReached = true;
        }
        if (!progress.isUtcReached && utcMs > 0 && utcMs >= gate.targetMs) {
            progress.isUtcReached = true;
        }
        const isPastGate = progress.isRtpReached && progress.isUtcReached;
        if (isPastGate) {
            this.clearStall(deviceId);
            this.planner.markDelivered(deviceId);
        }
        return { utcMs, isPastGate };
    }

    /**
   * A snapshot of one device's seek/anchor state for the diagnostics HUD:
   * newest sync anchor, the pending seek's target/seq/base, the UTC the server
   * acked the seek at, and whether a gate is armed. Seek/anchor fields only; the
   * HUD adds the clock, frame RTP and metadata that the display components own.
   * Plus the session's own liveness - phase, data channel, whether a track for
   * this device ever arrived (see debugHud connLine for how they are read).
   */
    getDiagnostics(deviceId: string): PlayerDiagnostics {
        const map = this.getSyncMap(deviceId);
        const anchor = map.length ? map[map.length - 1] : null;
        const gate = this.seekGate;
        return {
            phase: this.phase,
            isDcOpen: this.dc?.readyState === 'open',
            hasTrack: this.streams.has(deviceId),
            anchorUtcMs: anchor ? Math.round(anchor.utcUs / MILLISECOND_US) : null,
            anchorRtp: anchor ? anchor.rtpTs : null,
            seekTargetMs: gate ? gate.targetMs : this.planner.wirePositionMs,
            seekAckMs: this.seekAckMs.get(deviceId) ?? null,
            seekSeq: this.seekSeq,
            seekRtpBase: gate ? gate.base32 : null,
            hasRevealGate: !!gate,
            hasArchiveEnded: this.endedDevices.has(deviceId),
        };
    }

    /** Pauses/resumes every provider (per-device commands; the session has no
   *  session-wide transport). positionMs, on resume, makes the server reposition there
   *  first - a live pause resumes as archive exactly at the client's held frame - with the
   *  same RTP rebase and reveal gate a seek carries, so the pre-roll run stays hidden. */
    setPaused(isPaused: boolean, positionMs: number | null = null): void {
        if (this.isClosed || this.shouldBePaused === isPaused) { return; }
        this.shouldBePaused = isPaused;
        let ok = true;
        if (!isPaused && positionMs !== null) {
            this.syncMaps.clear();
            const base32 = this.getNextRtpBase();
            const seq = ++this.commandSeq;
            for (const id of this.getDeviceIds()) {
                ok = this.send({ resume: id, position: positionMs, seq,
                    rtpBase: base32 }) && ok;
            }
            if (ok) {
                this.setRepositionGate(positionMs, /*precise*/ true, seq, base32);
                this.startReplyWatchdog(seq);
                this.planner.notePosition(positionMs);
            } else {
                this.planner.desiredPositionMs = positionMs; //< The planner re-seeks there.
            }
            this.wasPauseSent = false;
            return;
        }
        const key = isPaused ? 'pause' : 'resume';
        const ids = this.getDeviceIds();
        const seq = ++this.commandSeq;
        for (const id of ids) { ok = this.send({ [key]: id, seq }) && ok; }
        if (ids.length > 0 && ok) { this.startReplyWatchdog(seq); }
        this.wasPauseSent = isPaused && ok;
    }

    /** Steps the paused session one video frame on every camera; direction < 0 steps
   *  back. One command per session (any device names it). Returns the command's
   *  sequence: the server answers with {seq, positions:[{deviceId, timestampUs}]} once
   *  the step has settled, and the panel takes each camera's paused position from it. */
    stepFrame(direction: number, positionMs: number | null = null): number | null {
        if (this.isClosed) { return null; }
        const [firstKey] = this.qualities.keys();
        if (!firstKey) { return null; }
        const seq = ++this.commandSeq;
        const command = { [direction < 0 ? 'previousFrame' : 'nextFrame']: firstKey, seq };
        // From a live pause the server has no archive position yet: the client's shown frame
        // rides along, and the server seeks there before stepping.
        if (positionMs !== null) {
            command.position = positionMs;
            this.planner.desiredPositionMs = positionMs;
        }
        // A step back (or a positioned step) makes the server re-run the whole GOP as pre-roll
        // in one burst; unpaced, it overruns the decoder's queue and the settled frame is never
        // decoded, so the tile stays frozen. Arm the receiver gates to pace it, as a seek does.
        if (direction < 0 || positionMs !== null) {
            this.videoGates.forEach((p) => p.worker.postMessage({ shouldPace: true }));
        }
        const ok = this.send(command);
        if (ok) { this.startReplyWatchdog(seq); }
        // A lost positioned step leaves the position due, so the planner re-seeks there.
        if (positionMs !== null && ok) {
            this.planner.notePosition(positionMs);
        }
        return seq;
    }

    /**
   * Starts the dead-session watchdog for a playback command just sent: the server answers
   * every one of them on the data channel, so silence past the timeout means the session no
   * longer exists server-side - a state a reveal gate armed for the command would otherwise
   * freeze the tiles in forever. A reply disarms it (see stopReplyWatchdogOnReply); a
   * deadline already running is kept - the first unanswered command started the silence.
   */
    startReplyWatchdog(seq: number): void {
        if (this.replyTimer !== undefined) { return; }
        this.replyAwaitSeq = seq;
        this.replyTimer = setTimeout(() => {
            this.replyTimer = undefined;
            console.warn('[PlayerConnection] no reply to a playback command; rebuilding');
            this.rebuild();
        }, kCommandReplyTimeoutMs);
    }

    stopReplyWatchdog(): void {
        if (this.replyTimer === undefined) { return; }
        clearTimeout(this.replyTimer);
        this.replyTimer = undefined;
    }

    /**
   * Disarms the watchdog on a message that answers a command: a stream status or a position
   * report. The server echoes the command's seq on those; a reply tagged with an OLDER seq
   * was in flight before the awaited command and proves nothing about it. A reply without a
   * seq comes from a server predating the echo and is trusted as the old heuristic did.
   * Media-driven messages (sync points, metadata) can sit buffered from before a session
   * died, so they never disarm.
   */
    stopReplyWatchdogOnReply(message: DataChannelFrame): void {
        if (this.replyTimer === undefined) { return; }
        switch (message.key) {
            case 'status':
            case 'positions': {
                const { seq } = message.frame;
                if (seq !== undefined && seq < this.replyAwaitSeq) { return; }
                this.stopReplyWatchdog();
                break;
            }
            case 'rtpTimestamp':
            case 'metadata':
                break;
            default:
                break;
        }
    }

    /** RTP↔UTC sync points of one device (newest last; [] before the first). */
    getSyncMap(deviceId: string): SyncPoint[] {
        return this.syncMaps.get(deviceId) ?? [];
    }

    /**
   * Commits one device's desired quality to the planner, which sends it once the wire is free
   * and it differs from what the server has. Invoked by the hook's periodic size monitor, which
   * debounces so transient sizes mid-animation never flip the stream.
   */
    commitQuality(deviceId: string): void {
        if (this.isClosed) { return; }
        const quality = this.qualities.get(deviceId);
        if (!quality) { return; }
        this.planner.setDesiredQuality(deviceId, quality);
        this.planner.plan();
    }

    /** The stream the server was last told to send for a device; null when it has none. */
    getDeliveredQuality(deviceId: string): StreamQuality | null {
        return this.planner.wireQuality(deviceId);
    }

    /** One data-channel frame; the key it carries decides where it goes. */
    onDataChannelFrame(message: DataChannelFrame): void {
        this.stopReplyWatchdogOnReply(message);
        switch (message.key) {
            case 'rtpTimestamp':
                this.addSyncPoint(message.frame);
                break;
            case 'metadata':
                this.publishPacket(message.frame);
                break;
            case 'positions':
                this.reportPositions(message.frame);
                break;
            case 'status':
                this.applyStreamStatus(message.frame);
                break;
            case 'videoTransport':
                this.setVideoTransport(message.frame);
                break;
            default:
                break;
        }
    }

    /**
     * The streams the tiles show: a camera whose video comes over the data channel shows the
     * picture decoded here, every other one the stream its track brought.
     */
    publishStreams(): void {
        const shownStreams = new Map(this.streams);
        for (const [deviceId, transport] of this.videoTransports) {
            const track = this.dataChannelVideo.get(deviceId);
            if (transport === VideoTransport.DataChannel && track !== undefined) {
                // The camera's audio still comes over RTP: the decoded picture plays it too.
                for (const audio of this.streams.get(deviceId)?.getAudioTracks() ?? []) {
                    if (!track.stream.getTrackById(audio.id)) { track.stream.addTrack(audio); }
                }
                shownStreams.set(deviceId, track.stream);
            }
        }
        this.onStreams(shownStreams);
    }

    /**
     * The server moved a camera's video between RTP and the data channel. Its word alone decides
     * what the tile shows: a frame late after it must not switch the tile back to a decoder that
     * only holds its last picture.
     */
    setVideoTransport(frame: VideoTransportFrame): void {
        const deviceId = this.byStrippedUuid.get(stripUuid(frame.deviceId));
        if (!deviceId) { return; }
        if (this.videoTransports.get(deviceId) === frame.videoTransport) { return; }
        this.videoTransports.set(deviceId, frame.videoTransport);
        if (frame.videoTransport === VideoTransport.Rtp) {
            // An idle 12 MP decoder holds its reference frames for nothing.
            this.closeDataChannelVideo(deviceId);
        }
        this.publishStreams();
    }

    /** A frame of a camera whose video comes over the data channel. */
    onVideoMessage(message: VideoMessage): void {
        const deviceId = this.byStrippedUuid.get(stripUuid(message.deviceId));
        if (!deviceId) { return; }
        // The frames ride unordered: one may land before the server's word on the transport,
        // which still decodes it, or late after the word "rtp", which drops it.
        const transport = this.videoTransports.get(deviceId);
        if (transport === VideoTransport.Rtp) { return; }
        let track = this.dataChannelVideo.get(deviceId);
        if (track === undefined) {
            const createdTrack = createDataChannelVideoTrack();
            if (createdTrack === null) { return; }
            track = createdTrack;
            this.dataChannelVideo.set(deviceId, track);
            this.audioClockTimer ??= setInterval(() => this.sendAudioClocks(), AUDIO_CLOCK_MS);
            if (transport === VideoTransport.DataChannel) { this.publishStreams(); }
        }
        track.push(message);
    }

    /** Ends a camera's decoded picture, with the camera, the session or a move to RTP. */
    closeDataChannelVideo(deviceId: string): void {
        const track = this.dataChannelVideo.get(deviceId);
        if (track !== undefined) {
            track.close();
            this.dataChannelVideo.delete(deviceId);
        }
        if (this.dataChannelVideo.size === 0) {
            clearInterval(this.audioClockTimer);
            this.audioClockTimer = undefined;
        }
    }

    /** The receiver of a camera's RTP audio; undefined for a camera without audio. */
    findAudioReceiver(deviceId: string): RTCRtpReceiver | undefined {
        const [audio] = this.streams.get(deviceId)?.getAudioTracks() ?? [];
        if (audio === undefined) { return undefined; }
        return this.pc?.getReceivers().find((receiver) => receiver.track === audio);
    }

    /** Tells each data channel video track the audio its camera plays now, the frames' clock. */
    sendAudioClocks(): void {
        for (const [deviceId, track] of this.dataChannelVideo) {
            const receiver = this.findAudioReceiver(deviceId);
            const syncPoint = findAudioSyncPoint(this.syncMaps.get(deviceId) ?? []);
            track.setAudioClock(receiver ? readAudioClock(receiver, syncPoint) : null);
        }
    }

    /** The tiles map presented frames to absolute time through these. */
    addSyncPoint(frame: SyncPointFrame): void {
        const deviceId = this.byStrippedUuid.get(stripUuid(frame.deviceId));
        if (!deviceId) { return; }
        let map = this.syncMaps.get(deviceId);
        if (!map) {
            map = [];
            this.syncMaps.set(deviceId, map);
        }
        map.push({
            rtpTs: frame.rtpTimestamp,
            utcUs: frame.timestampUs,
            ...(frame.audio === undefined ? {} : {
                audio: { rtpTs: frame.audio.rtpTimestamp, utcUs: frame.audio.timestampUs },
            }),
        });
        if (map.length > kMaxSyncEntries) { map.shift(); }
    }

    /** Routes a packet to the tile of the camera it names. */
    publishPacket(packet: MetadataPacket): void {
        const deviceId = this.byStrippedUuid.get(stripUuid(packet.metadata.deviceId));
        if (deviceId) { publishMetadata(deviceId, packet); }
    }

    /** Where each camera's paused playback settled after the frame step sent with that seq. */
    reportPositions(frame: PositionsFrame): void {
        const byDevice = new Map<string, number>();
        for (const report of frame.positions) {
            const deviceId = this.byStrippedUuid.get(stripUuid(report.deviceId));
            if (deviceId) {
                byDevice.set(deviceId, report.timestampUs / MILLISECOND_US);
            }
        }
        this.onPosition?.(frame.seq, byDevice);
    }

    applyStreamStatus(frame: StreamStatusFrame): void {
        const deviceId = this.byStrippedUuid.get(stripUuid(frame.deviceId));
        switch (frame.status) {
            // The stream needs a session reconnect (codec change, renegotiation fallback); with
            // several cameras on one session the only safe response is a full rebuild.
            case StreamStatus.MovedPermanently:
                this.rebuild();
                break;
            // The device's archive ended while the session plays on: the tile shows "No data"
            // until playback moves again.
            case StreamStatus.Gone:
                if (!deviceId) { return; }
                if (!this.endedDevices.has(deviceId)) {
                    this.endedDevices.set(deviceId, Date.now());
                }
                this.onStreamEof?.(deviceId);
                this.planner.markDelivered(deviceId);
                break;
            // The server could not seek that device (no provider for the moment). It will show no
            // frame for this seek, so the planner stops waiting for it.
            case StreamStatus.NotFound:
                if (deviceId) { this.planner.markDelivered(deviceId); }
                break;
            // The server's seek confirmation. Display does not depend on it (the reveal gate is
            // frame-driven), but it settles the seek's round so the next command need not wait for
            // a frame, and it is the only report of where the seek landed, so the diagnostics HUD
            // keeps it. Every command is answered with 200, so only the reply echoing the last
            // seek's seq confirms it; a server predating the echo sends none.
            case StreamStatus.Ok:
                if (!deviceId) { return; }
                // A camera set's ack settles its round directly: the server has switched, so a
                // rapid burst's next command must not wait behind an abandoned intermediate
                // camera's reveal gate or the timeout. The reveal gate still drives when each
                // camera's picture appears, independent of the planner.
                if (frame.seq !== undefined && frame.seq === this.switchSeq) {
                    this.planner.markDelivered(deviceId);
                    return;
                }
                if (frame.seq !== undefined && frame.seq !== this.seekSeq) { return; }
                if (frame.timestampMs >= 0) { this.seekAckMs.set(deviceId, frame.timestampMs); }
                this.planner.markDelivered(deviceId);
                break;
            default:
                break;
        }
    }

    send(obj: unknown): boolean {
        if (this.dc?.readyState === 'open') {
            try { this.dc.send(JSON.stringify(obj)); return true; } catch { return false; }
        }
        return false;
    }

    /**
     * Turns talk-back on/off for a SET of cameras on this server - one command carrying the
     * whole set, so the server's set-union/dedup engine (TalkRoute keyed by the sorted set)
     * builds exactly one talker for them. A single-camera caller passes a one-element array;
     * the wire shape is the same either way.
     */
    setTalk(deviceIds: string[], isOn: boolean): boolean {
        return this.send({ talk: { deviceIds, on: isOn } });
    }

    /**
     * Sends one Opus packet for talk-back on this same data channel - the toolbox pattern, one
     * warm bidirectional connection rather than a separate one. The frame is the camera's 16 raw
     * UUID bytes then the Opus; the media_server's Streamer routes it by that prefix (nx_webrtc
     * TalkHandlers). Silently dropped until the channel is open.
     */
    sendTalkAudio(deviceId: string, opus: ArrayBuffer): boolean {
        if (this.dc?.readyState !== 'open') { return false; }
        const id = uuidToBytes(deviceId);
        if (!id) { return false; }
        const frame = new Uint8Array(id.length + opus.byteLength);
        frame.set(id, 0);
        frame.set(new Uint8Array(opus), id.length);
        try { this.dc.send(frame); return true; } catch { return false; }
    }

    /** The mid of a device's video m-line; a device without a stream yet sorts after every one
   *  with. */
    getMid(deviceId: string): number {
        const streamId = this.streams.get(deviceId)?.id;
        const mid = streamId === undefined ? undefined : this.midByStreamId.get(streamId);
        return mid !== undefined && Number.isFinite(mid) ? mid : Number.MAX_SAFE_INTEGER;
    }

    /** Records the device set the player wants (order matters: index 0 identifies the session
   *  on a rebuild); the planner carries the difference to the wire. */
    setDevices(devices: DeviceStream[]): void {
        if (this.isClosed) { return; }
        this.qualities = new Map(devices.map((d) => [d.deviceId, d.quality ?? StreamQuality.Low]));
        if (devices.length === 0) {
            this.planner.setDesiredCameras([]);
            this.teardownPeer();
            return;
        }
        // Quality changes of retained devices are NOT committed here: the hook's debounced
        // monitor commits them once the size has held still for two ticks (tiles animate through
        // transient sizes that must not flip the stream). An arriving camera opens at its own.
        const committedMap = this.planner.desiredQualities;
        this.planner.setDesiredCameras([...this.qualities].map(([deviceId, quality]) =>
            ({ deviceId, quality: committedMap.get(deviceId) ?? quality })));
        this.planner.plan();
    }

    /**
   * Carries a camera set to the wire in one command: a departing camera is paired with an
   * arriving one, whose tile takes over its m-line, and the switch positions the arrivals at
   * positionMs. False when the channel took nothing.
   */
    applyDeviceSet(addedTexts: string[], droppedIds: string[],
        positionMs: number | null): boolean {
        const nextMap = this.planner.desiredQualities;
        // Departures lowest mid first: the server hands the freed m-lines to the arriving cameras in
        // that order, and the pairing below must bind each tile to the m-line it actually gets.
        const removedIds = [...droppedIds].sort((a, b) => this.getMid(a) - this.getMid(b));
        this.byStrippedUuid = new Map([...nextMap.keys()].map((id) => [stripUuid(id), id]));
        // Pair each departing device with an arriving one: the server retargets that m-line in place
        // (same ssrc and MediaStream, new content, no fresh ontrack), so carry the stream over to the
        // new device key instead of dropping it, and let the demux resolve its unchanged id to the new
        // device too. Extra arrivals/departures beyond the pairs fall through to add/remove as before.
        const retargetCount = Math.min(addedTexts.length, removedIds.length);
        for (let i = 0; i < retargetCount; i += 1) {
            const removedId = removedIds[i];
            const newId = addedTexts[i];
            if (removedId === undefined || newId === undefined) { continue; }
            const stream = this.streams.get(removedId);
            if (stream) {
                this.streams.set(newId, stream);
                this.byStrippedUuid.set(stripUuid(stream.id), newId);
            } else {
                // The departing m-line's track has not arrived yet - a switch right after a
                // rebuild, whose ontrack lags the command. Its msid is the departing camera's
                // uuid, so route that to the arriving device: the late ontrack then binds to the
                // new tile instead of resolving to nothing and being dropped (a frozen tile).
                this.byStrippedUuid.set(stripUuid(removedId), newId);
            }
        }
        const retargetedIds = new Set(removedIds.slice(0, retargetCount));
        for (const id of removedIds) {
            // A retargeted stream keeps its receiver and gate worker; a genuinely-removed one must end its
            // worker here, or the closed tile's clone keeps a MediaStreamTrackProcessor alive.
            if (!retargetedIds.has(id)) {
                const streamId = this.streams.get(id)?.id;
                if (streamId) { this.removeVideoGate(streamId); }
            }
            this.closeDataChannelVideo(id);
            this.streams.delete(id); //< A retargeted stream was already copied to its new key above.
            this.videoTransports.delete(id);
            this.metadataOn.delete(id);
            this.syncMaps.delete(id);
            this.seekAckMs.delete(id);
            this.switchBases.delete(id);
            this.endedDevices.delete(id);
            this.codecUnsupportedByDevice.delete(id);
            this.clearStall(id);
            this.onPublishers([id], false);
        }
        if (removedIds.length || retargetCount) { this.publishStreams(); }
        return this.sendCameraSet(addedTexts, addedTexts.slice(0, retargetCount), positionMs);
    }

    /**
   * One atomic command with the full desired set (+ the archive position, if the player is parked
   * there): the server diffs it and switches in a single offer round, instead of a fast
   * add-then-remove that races two renegotiations. The `seek` positions the cameras this switch
   * newly adds; already-present cameras keep playing.
   *
   * `retargetedIds` are the arrivals that took a departing camera's m-line, whose receivers must
   * drop what is still queued from that camera.
   */
    sendCameraSet(addedTexts: string[], retargetedIds: string[],
        positionMs: number | null): boolean {
        const nextMap = this.planner.desiredQualities;
        this.onPublishers(addedTexts, true);
        const cameras = [...nextMap.entries()].map(([id, q]) => ({ deviceId: id,
            stream: streamIndexOf(q) }));
        // A client-chosen RTP base a gap above every track's wire: the server rebases each retargeted
        // camera there, so the switch's frames land above the base and the previous camera's below it -
        // the discriminator the worker gate drops by (and the browser reveal gate hides by).
        const base32 = this.getNextRtpBase();
        const seq = ++this.commandSeq;
        const msg: {
            setCameras: { deviceId: string; stream: number }[];
            seq: number;
            rtpBase: number;
            seek?: number;
        } = { setCameras: cameras, seq, rtpBase: base32 };
        if (positionMs !== null) {
            msg.seek = this.getResumePositionMs() ?? positionMs;
        }
        if (!this.send(msg)) { return false; }
        this.switchSeq = seq;
        {
            // Arm the drop on exactly the receivers this switch rebases (the retarget pairs, whose
            // streams were re-keyed to the arriving device above): their worker discards the previous
            // camera's frames until one at or above base32 arrives, and paces the burst.
            for (const id of retargetedIds) {
                const streamId = this.streams.get(id)?.id;
                this.videoGates.forEach((p) => {
                    if (p.streamId === streamId) {
                        p.worker.postMessage({ dropBelow: base32 });
                    }
                });
                // What the worker cannot catch: frames of the camera leaving that are already
                // decoded and queued. Classifying them keeps its picture off the arriving tile.
                this.switchBases.set(id, base32);
            }
            // The shared position rode the setCameras message; a parked-paused player still has to
            // pause the cameras it just added (pause is per-device, not part of the set).
            if (positionMs !== null && this.shouldBePaused) {
                for (const id of addedTexts) { this.send({ pause: id }); }
            }
        }
        return true;
    }

    /**
   * Live stream facts for one camera's video track, read from WebRTC stats:
   * {codec, width, height, fps, packets, framesDecoded} or null while
   * unavailable. Powers the layout tile's info chip and the diagnostics HUD;
   * callers poll while shown. The raw counters ride along off the same
   * inbound-rtp record because they separate what the others cannot: a server
   * sending nothing (packets stuck) from a decoder receiving and not decoding
   * (packets climbing, framesDecoded stuck) both leave codec/size/fps empty.
   */
    async getDeviceInfo(deviceId: string): Promise<DeviceStreamInfo | null> {
        if (this.videoTransports.get(deviceId) === VideoTransport.DataChannel) {
            return this.dataChannelVideo.get(deviceId)?.getInfo() ?? null;
        }
        const pc = this.pc;
        const track = this.streams.get(deviceId)?.getVideoTracks?.()[0];
        if (!pc || !track) { return null; }
        try {
            // Selected by track, so at most one video inbound-rtp is in the report;
            // the report is itself keyed by id, so its codec needs no second index.
            const stats = await pc.getStats(track);
            let inbound = null;
            for (const report of stats.values()) {
                if (report.type === 'inbound-rtp' && report.kind === 'video') {
                    inbound = report;
                    break;
                }
            }
            if (!inbound) { return null; }
            const mime = stats.get(inbound.codecId)?.mimeType ?? '';
            return {
                codec: mime.replace(/^video\//i, '') || null,
                width: inbound.frameWidth ?? 0,
                height: inbound.frameHeight ?? 0,
                fps: inbound.framesPerSecond !== undefined ? Math.round(inbound.framesPerSecond) : null,
                packets: inbound.packetsReceived ?? null,
                framesDecoded: inbound.framesDecoded ?? null,
            };
        } catch {
            return null;
        }
    }

    /** Per-camera overlay metadata subscription (scoped {metadata, deviceId}). */
    setDeviceMetadata(deviceId: string, isEnabled: boolean): void {
        if (this.isClosed || !this.qualities.has(deviceId)) { return; }
        if (!!this.metadataOn.get(deviceId) === isEnabled) { return; }
        this.metadataOn.set(deviceId, isEnabled);
        this.send({ metadata: isEnabled, deviceId });
    }

    rebuild(): void {
        if (this.isClosed) { return; }
        this.connect();
    }

    /**
     * Rebuilds the session for a camera whose tile waits for a picture that does not come while
     * the session reports itself connected. No transport event reports this failure, and a
     * rebuild clears it. The tiles of one session stall together, so one rebuild serves them all.
     * A camera that shows nothing on STALL_REBUILDS fresh sessions has a fault of its own: its
     * tile reports a failure, and its siblings keep their picture.
     */
    rebuildOnStall(deviceId: string): void {
        if (this.isClosed || !this.qualities.has(deviceId) || this.noPicture.has(deviceId)) {
            return;
        }
        const now = Date.now();
        if (now - this.stallRebuildAtMs < STALL_RETRY_MS) {
            return;
        }
        const lastValue = this.stallRetries.get(deviceId);
        if (lastValue && now - lastValue.atMs < lastValue.waitMs) {
            return;
        }
        if (lastValue && lastValue.rebuilds >= STALL_REBUILDS) {
            this.noPicture.add(deviceId);
            this.emitState(this.phase);
            return;
        }
        this.stallRetries.set(deviceId, {
            atMs: now,
            waitMs: lastValue ? lastValue.waitMs * 2 : STALL_RETRY_MS,
            rebuilds: (lastValue?.rebuilds ?? 0) + 1,
        });
        this.stallRebuildAtMs = now;
        console.warn('[PlayerConnection] no picture on a connected session; rebuilding', deviceId);
        this.rebuild();
    }

    /** Ends the stall record of a camera that shows a picture or leaves the session. */
    clearStall(deviceId: string): void {
        this.stallRetries.delete(deviceId);
        if (this.noPicture.delete(deviceId)) {
            this.emitState(this.phase);
        }
    }

    scheduleRetry(): void {
        if (this.isClosed || this.retryTimer) { return; }
        this.retryAttempt += 1;
        const plan = getConnectionRetryPlan(this.retryAttempt);
        this.emitState(ConnectionPhase.Retrying, plan.shouldShowError);
        this.retryTimer = setTimeout(() => {
            this.retryTimer = undefined;
            this.connect();
        }, plan.delayMs);
    }

    cancelRetry(): void {
        clearTimeout(this.retryTimer);
        this.retryTimer = undefined;
    }

    /** Skips the wait of a pending retry; a connection that plays has no retry to skip. */
    retryNow(): void {
        if (this.isClosed || this.retryTimer === undefined) {
            return;
        }
        this.cancelRetry();
        this.connect();
    }

    emitState(phase: ConnectionPhase, shouldShowError = false): void {
        this.phase = phase;
        this.onState?.({
            phase,
            shouldShowError,
            codecUnsupportedByDevice: Object.fromEntries(this.codecUnsupportedByDevice),
            noPictureDevices: [...this.noPicture],
        });
    }

    teardownPeer(): void {
        clearTimeout(this.handshakeTimer);
        this.stopReplyWatchdog();
        // The command on the wire died with the session; the next one is the difference the new
        // session's channel finds when it opens.
        this.planner.onSessionClosed();
        if (this.ws) {
            const ws = this.ws;
            this.ws = null;
            ws.onclose = null;
            ws.onmessage = null;
            try { ws.close(); } catch { /* already closed */ }
        }
        if (this.pc) {
            const pc = this.pc;
            this.pc = null;
            this.dc = null;
            pc.ontrack = null;
            pc.ondatachannel = null;
            pc.onconnectionstatechange = null;
            try { pc.close(); } catch { /* already closed */ }
        }
        // Drop this pc's gate workers now, so a rebuild/reconnect series does not accumulate stale
        // workers/clones. The next pc's ontrack builds fresh ones.
        this.videoGates.forEach((p) => this.stopVideoGate(p));
        this.videoGates.clear();
        this.streams.clear();
        for (const deviceId of [...this.dataChannelVideo.keys()]) {
            this.closeDataChannelVideo(deviceId);
        }
        this.videoTransports.clear();
        this.midByStreamId.clear();
        this.sdpChain = Promise.resolve();
        // pc.close() ended every track, so the streams the owner still holds are dead. Say so, or
        // the tiles keep a frozen last frame and read as playing (PlayerVideo rebinds on a
        // DIFFERENT object, never on their silence); the next session's ontrack brings new ones.
        this.onStreams(new Map());
    }

    /** Stop one gate worker: end its probe clone (and its MediaStreamTrackProcessor reader) and
   *  terminate the worker. */
    stopVideoGate(videoGate: Partial<VideoGate>): void {
        try { videoGate.probeTrack?.stop(); } catch { /* already stopped */ }
        try { videoGate.worker?.terminate(); } catch { /* already gone */ }
    }

    /** Tear down and forget the gate worker of a genuinely-removed stream (a retargeted stream reuses
   *  its receiver and keeps its worker, so its streamId is never passed here). */
    removeVideoGate(streamId: string): void {
        for (const videoGate of this.videoGates) {
            if (videoGate.streamId !== streamId) {
                continue;
            }
            this.stopVideoGate(videoGate);
            this.videoGates.delete(videoGate);
        }
    }

    close(): void {
        if (this.isClosed) { return; }
        this.isClosed = true;
        this.cancelRetry();
        this.onPublishers(this.getDeviceIds(), false);
        this.teardownPeer();
    }
}
